import { Autocomplete, Stack, TextField, Typography, Popper, Box } from "@mui/material";
import { useEffect, useState } from "react";
import { useTBContext } from "../../Contexts/useTBStates";
import { IOSSwitch } from "../../../shared/Components/iosSwitch";
import { StyledIconButton } from "../../../shared/Components/StyledIconButton";
import DeleteIcon from '@mui/icons-material/Delete';

interface HdcpItemDisplayProps {
    level: 'event' | 'round';
    orient: 'row' | 'row-reverse' | 'column' | 'column-reverse';
    label: string;
    keyName: string;
    composite?: boolean;
    labelField?: string;
    optionsKeyName?: string;
    freeSolo?: boolean;
    hideLabel?: boolean;
    flipped?: boolean;
    extraFactor?: number | 1;
    locked?: boolean;
    removable?: boolean;
    defaultValue?: any;
}


/** Primary hdcp usage is the first array element; supports legacy object shape. */
const getPrimaryHdcpUsage = (hdcp: unknown) => Array.isArray(hdcp) ? hdcp[0] : (hdcp as object | undefined);

export const HdcpItemDisplay = ({ 
       level, orient, label, keyName, labelField, optionsKeyName, freeSolo, hideLabel, flipped, extraFactor, locked, removable, defaultValue, composite
}: HdcpItemDisplayProps) => {

    const { viewEvent, setViewEvent,
        factor, roundValue,
        appTheme, 
        editMode,
        forceUpdate,
        optionsLoaded,
    } = useTBContext();
    
    const thisOptions = optionsLoaded.find((o: any) => o.keyName === optionsKeyName)?.options || [];

    const prim = level === 'event' ? getPrimaryHdcpUsage(viewEvent.hdcpUsage) : getPrimaryHdcpUsage(viewEvent.rounds[roundValue-1]?.hdcpUsage);
    const hdcpUsage = composite ? prim?.hdcpComposite : prim;
    //console.log('hdcpUsage', level, composite, keyName, hdcpUsage);

    function formatEditValue() {

        let newValue = hdcpUsage?.[keyName];

        if (newValue === undefined || newValue === null && defaultValue) {
            newValue = defaultValue;
        }

        return newValue;
    }

    function formatDisplayValue(value?: any) {

        let newValue = value ?? hdcpUsage?.[keyName];

        if (newValue === undefined || newValue === null && defaultValue) {
            newValue = defaultValue;
        }

        else if (typeof newValue === 'string' && !isNaN(Number(newValue))) {
            newValue = Number(newValue);
        }
        else {
            newValue = newValue;
        }

        return newValue;
    }

    function getDataType() {
        let value = hdcpUsage?.[keyName];

        if (value === undefined || value === null && defaultValue) {
            value = defaultValue;
        }
        
        
        // If value doesn't exist, default to string    
        if (value === undefined || value === null) {
            return 'string';
        }
        
        // Then check other types
        if (typeof value === 'number' || (typeof value === 'string' && /^\d+$/.test(value.trim()))) {
            return 'number';
        } else if (typeof value === 'boolean') {
            return 'boolean';
        }
        
        return 'string';
    }

    const [blockValue, setBlockValue] = useState<any>(formatEditValue());
    const [displayValue, setDisplayValue] = useState<any>(formatDisplayValue());
    const [dataType, setDataType] = useState<string>(getDataType());

    useEffect(() => {
        setBlockValue(formatEditValue());
        setDisplayValue(formatDisplayValue());
    }, [forceUpdate]);
    
    const getDefaultUsage = (value: string) => optionsLoaded?.find((o: any) => o.keyName === 'HdcpTypes')?.options?.find((o: any) => o.type === value)?.defaultUsage;

    const setNewDefaultUsage = (value: string) => {
        const defaultUsage = getDefaultUsage(value);
        if (!defaultUsage) return;

        if (level === 'event') {
            const usageArr = [defaultUsage];
            const updatedRounds = viewEvent.rounds.map((round: any) => ({ ...round, hdcpUsage: usageArr }));
            setViewEvent((prevState: any) => ({ ...prevState, hdcpUsage: usageArr, rounds: updatedRounds }));
        } else {
            const updatedRounds = viewEvent.rounds.map((round: any) =>
                round.roundNumber === roundValue ? { ...round, hdcpUsage: [defaultUsage] } : round
            );
            setViewEvent((prevState: any) => ({ ...prevState, rounds: updatedRounds }));
        }
    }

    // Handles change to any other handicap item
    const setUpdatedHdcpUsage = (value: any) => {

        console.log('HDCP Update Change triggered by: ', level, composite, keyName, value);
        // If composite, sets the new value to the composite object
        if (composite) {
            if (level === 'event') {
                const prim = getPrimaryHdcpUsage(viewEvent.hdcpUsage);
                const updatedPrim = { ...prim, hdcpComposite: { ...prim?.hdcpComposite, [keyName]: value || '' } };
                const updatedRounds = viewEvent.rounds.map((round: any) => {
                    const rPrim = getPrimaryHdcpUsage(round.hdcpUsage);
                    return { ...round, hdcpUsage: [{ ...rPrim, hdcpComposite: { ...rPrim?.hdcpComposite, [keyName]: value || '' } }] };
                });
                setViewEvent((prevState: any) => ({ ...prevState, hdcpUsage: [updatedPrim], rounds: updatedRounds }));
            } else {
                const updatedRounds = viewEvent.rounds.map((round: any) => {
                    if (round.roundNumber === roundValue) {
                        const rPrim = getPrimaryHdcpUsage(round.hdcpUsage);
                        return { ...round, hdcpUsage: [{ ...rPrim, hdcpComposite: { ...rPrim?.hdcpComposite, [keyName]: value || '' } }] };
                    }
                    return round;
                });
                setViewEvent((prevState: any) => ({ ...prevState, rounds: updatedRounds }));
            }
        }
        else {
            if (level === 'event') {
                const prim = getPrimaryHdcpUsage(viewEvent.hdcpUsage);
                const updatedPrim = { ...prim, [keyName]: value || '' };
                const updatedRounds = viewEvent.rounds.map((round: any) => {
                    const rPrim = getPrimaryHdcpUsage(round.hdcpUsage);
                    return { ...round, hdcpUsage: [{ ...rPrim, [keyName]: value || '' }] };
                });
                setViewEvent((prevState: any) => ({ ...prevState, hdcpUsage: [updatedPrim], rounds: updatedRounds }));
            } else {
                const updatedRounds = viewEvent.rounds.map((round: any) => {
                    if (round.roundNumber === roundValue) {
                        const rPrim = getPrimaryHdcpUsage(round.hdcpUsage);
                        return { ...round, hdcpUsage: [{ ...rPrim, [keyName]: value || '' }] };
                    }
                    return round;
                });
                setViewEvent((prevState: any) => ({ ...prevState, rounds: updatedRounds }));
            }
        }
    }
    
    return (
        <>
            {editMode && !locked ? (
                <Stack
                    direction={orient}
                    spacing={1}
                    sx={{
                        width: '100%',
                        justifyContent: 'space-between',
                        alignItems: orient === 'row' || orient === 'row-reverse' ? 'center' : 'flex-start',
                    }}
                >
                    {!hideLabel && 
                        <Typography 
                            flex={2} 
                            width={orient === 'row' ? 'auto' : '100%'} 
                            textAlign="left"
                            sx={{ 
                                fontSize: 14*factor*(extraFactor || 1), 
                                fontWeight: 500,
                                lineHeight: 1.4,
                                display: 'flex',
                                alignItems: 'center',
                            }}>
                            {label}:
                            </Typography>}
                    {thisOptions && thisOptions.length > 0 && labelField && (
                           
                                <Autocomplete
                                fullWidth
                                freeSolo={freeSolo || false}
                                disableClearable={true}
                                options={thisOptions}
                                getOptionLabel={(option) => option[labelField]}
                                value={thisOptions.find((o: any) => o[labelField] === displayValue) || null}
                                onChange={(e, newValue) => {
                                    console.log('New Value', newValue);
                                    setBlockValue(newValue[labelField] || '')
                                    setDisplayValue(newValue[labelField] || '')
                                    
                                    if (keyName === 'type' && !composite) {
                                        setNewDefaultUsage(newValue[labelField] || '');
                                    }
                                    else {
                                        setUpdatedHdcpUsage(newValue[labelField] || '');
                                    }
                                        
                                    /* } */
                                }}
                                sx={{
                                    flex: 3,
                                    padding: 0,
                                    '& .MuiAutocomplete-inputRoot': {
                                        padding: 0,
                                    }
                                }}
                                renderInput={(params) => <TextField 
                                    {...params} 
                                    variant="outlined" 
                                    sx={{
                                        padding: 0,
                                        "& .MuiInputBase-input": { fontSize: 14*factor*(extraFactor || 1), height: 14*factor*(extraFactor || 1), padding: 1 },
                                        '& .MuiOutlinedInput-root': {padding: '0px 0px'},
                                        '& input': {textAlign: 'left', lineHeight: 'normal'},
                                        flex: 3,
                                        width: '100%'
                                    }}
                                    inputProps={{
                                        ...params.inputProps,
                                        inputMode: "text",
                                        autoComplete: "off",
                                        sx: {
                                            fontSize: 14 * (factor ? factor : 1) ,
                                            /* '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: appTheme.palette.primary.main,
                                            }, */
                                        },
                                    }}
                                    InputLabelProps={{
                                        sx: { fontSize: 16 * (factor ? factor : 1) * (extraFactor || 1), lineHeight: 'normal', padding: '0px 10px', backgroundColor: 'whitesmoke', marginTop: '0px' },
                                        shrink: true
                                    }}
                                />}
                                PopperComponent={(props) => (
                                    <Popper {...props} autoFocus sx={{ 
                                        zIndex: 1300, // Ensure it appears above other elements
                                        '& .MuiAutocomplete-listbox': {
                                            maxHeight: 200,
                                            overflowY: 'auto',
                                            bgcolor: 'whitesmoke',
                                        },
                                        '& .MuiAutocomplete-option': {
                                            padding: '5px 10px',
                                            '&:hover': {
                                                backgroundColor: `${appTheme.palette.primary.extraLight} !important`,
                                                borderRadius: 3
                                            },
                                            fontSize: 14 * (factor ? factor : 1),
                                        },
                                        '& .Mui-focusVisible' : {
                                                backgroundColor: `${appTheme.palette.primary.extraLight} !important`,
                                                borderRadius: 3,
                                        },
                                        
                                    }} />
                                    
                            )}
                        />
                        
                    )}
                    {(!thisOptions || thisOptions.length === 0) && dataType !== 'boolean' && (
                            <TextField
                                value={dataType === 'date' ? blockValue : displayValue}
                                type={dataType}
                                onChange={(e) => {
                                    const newValue = e.target.value;
                                    console.log(newValue);
                                    const formattedValue = dataType === 'number' ? Number(newValue) : newValue;
                                    setBlockValue(formattedValue);
                                    setDisplayValue(dataType === 'number' ? formattedValue : formatDisplayValue(formattedValue));
                                    
                                    setUpdatedHdcpUsage(dataType === 'number' ? formattedValue : formatDisplayValue(formattedValue));
                                }}
                                sx={{
                                    padding: 0,
                                    "& .MuiInputBase-input": { fontSize: 14*factor*(extraFactor || 1), height: 14*factor*(extraFactor || 1), padding: 1 },
                                    '& .MuiOutlinedInput-root': {padding: '0px 0px'},
                                    '& input': {textAlign: 'left', lineHeight: 'normal'},
                                    flex: 3,
                                    width: '100%',
                                }}
                                InputProps={{
                                    sx: {
                                        fontSize: 14*factor*(extraFactor || 1),
                                    },
                                }}
                            />
                        )}
                    {dataType === 'boolean' && (
                        <Stack direction='row' flex={3} spacing={1} alignItems='center'>
                            <Typography sx={{ fontSize: 14*factor*(extraFactor || 1), fontWeight: 500 }}>No</Typography>
                            <IOSSwitch
                                checked={displayValue}
                                onChange={(e) => {
                                    setBlockValue(e.target.checked);
                                    setDisplayValue(e.target.checked);
                                    setUpdatedHdcpUsage(e.target.checked);
                                }}
                            />
                            <Typography sx={{ fontSize: 14*factor*(extraFactor || 1), fontWeight: 500 }}>Yes</Typography>
                        </Stack>
                    )}
                    </Stack>
            ) : 
            (
            <Stack
                direction={orient}
                spacing={orient === 'row' ? 1 : 0.5}
                sx={{
                    width: '100%',
                    justifyContent: 'flex-start',
                    alignItems: orient === 'row' || orient === 'row-reverse' ? 'center' : 'flex-start',
                }}
            >
                {!hideLabel && <Typography 
                    flex={2} 
                    width={orient === 'row' ? 'auto' : '100%'} 
                    textAlign="left"
                    padding={0}
                    sx={{ 
                        fontWeight: 500, 
                        fontSize: 14*factor*(extraFactor || 1),
                        lineHeight: 1.4,
                        minHeight: 24*factor*(extraFactor || 1),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                    }}>
                    {label}:
                </Typography>}
                {dataType !== 'boolean' && (
                    <Typography 
                        flex={3} 
                        width={orient === 'row' ? 'auto' : '100%'} 
                        textAlign={flipped ? 'right' : 'left'} 
                        padding={0}
                        sx={{ 
                            fontSize: 14*factor*(extraFactor || 1),
                            lineHeight: 1.4,
                            minHeight: 24*factor*(extraFactor || 1),
                            display: 'flex',
                            alignItems: 'center',
                        }}>
                        {displayValue}
                    </Typography>

                )}
                {dataType === 'boolean' && (
                    <Stack direction='row' flex={3} spacing={1} alignItems='center' justifyContent='flex-start'>
                        <Typography sx={{ fontSize: 14*factor*(extraFactor || 1), fontWeight: 500 }}>No</Typography>
                        <IOSSwitch
                            checked={blockValue}
                            disabled
                        />
                        <Typography sx={{ fontSize: 14*factor*(extraFactor || 1), fontWeight: 500 }}>Yes</Typography>
                    </Stack>
                )}
            </Stack>
            )}
        </>
    )
}
